import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Chip,
  Card,
  CardContent,
  IconButton,
} from "@mui/material";
import { Close, Info } from "@mui/icons-material";
import { useTheme } from "../../context/ThemeContext";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import styles from "./ManualTagDialog.module.scss";

const ManualTagDialog = ({ open, onClose, onConfirm, fileName }) => {
  const { isDarkMode } = useTheme();


  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: "#e36250 !important",
      },
      secondary: {
        main: "#8b5cf6",
      },
    },
  });

  const [primaryTag, setPrimaryTag] = useState("");
  const [secondaryTags, setSecondaryTags] = useState([]);


  // Available tags with their colors and icons
  const availableTags = [
    { id: "SOP", label: "SOP", color: "primary", icon: "📄" },
    { id: "BP", label: "BP", color: "secondary", icon: "⭐" },
    { id: "POLICY", label: "POLICY", color: "primary", icon: "📄" },
    { id: "TRAINING", label: "TRAINING", color: "success", icon: "🎓" },
    { id: "TP", label: "TP", color: "warning", icon: "🔗" },
    { id: "NICHE", label: "NICHE", color: "primary", icon: "🎯" },
    { id: "DNS", label: "DNS", color: "default", icon: "🚫" },
    { id: "TEMPLATE", label: "TEMPLATE", color: "secondary", icon: "📄" },
    { id: "ADV", label: "ADV", color: "warning", icon: "💡" },
    { id: "TRADE_SECRET", label: "TRADE_SECRET", color: "error", icon: "🔒" },
    { id: "ORG", label: "ORG", color: "secondary", icon: "🏢" },
    { id: "PERSONA", label: "PERSONA", color: "success", icon: "👤" },
    { id: "CALL_LOG", label: "CALL_LOG", color: "success", icon: "📞" },
    { id: "DELIVERY", label: "DELIVERY", color: "warning", icon: "📦" },
    { id: "TLD", label: "TLD", color: "secondary", icon: "👥" },
  ];

  // Processing rules with their associated tags
  const processingRules = [
    {
      title: "Vectorized + Preserved (Compliance)",
      description: "Vectorized for search and preserved for compliance requirements",
      borderColor: "primary",
      tags: ["SOP", "TRADE_SECRET"],
    },
    {
      title: "Vectorized + Preserved",
      description: "Vectorized for search but preserved for reference",
      borderColor: "secondary",
      tags: ["BP", "POLICY", "TEMPLATE"],
    },
    {
      title: "Vectorized + Summarized",
      description: "Vectorized and summarized for efficient learning",
      borderColor: "success",
      tags: ["TRAINING", "CALL_LOG"],
    },
    {
      title: "Vectorized Only",
      description: "Vectorized for analysis, preservation varies by type",
      borderColor: "warning",
      tags: ["TP", "NICHE"],
    },
  ];

  const handlePrimaryTagChange = (event) => {
    setPrimaryTag(event.target.value);
  };

  const handleSecondaryTagToggle = (tagId) => {
    setSecondaryTags(prev => {
      if (prev.includes(tagId)) {
        return prev.filter(id => id !== tagId);
      } else {
        return [...prev, tagId];
      }
    });
  };

  const handleConfirm = () => {
    if (primaryTag) {
      onConfirm({
        primaryTag,
        secondaryTags,
        fileName,
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setPrimaryTag("");
    setSecondaryTags([]);
    onClose();
  };

  const getTagById = (tagId) => availableTags.find(tag => tag.id === tagId);

  if (!open) {
    return null;
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        className={styles.dialog}
        PaperProps={{
          className: styles.dialogPaper,
        }}
        disableEscapeKeyDown={false}
        disableBackdropClick={false}
      >
        <DialogTitle className={styles.dialogTitle}>
          <Typography variant="h6" className={styles.title}>
            Manual Tag Selection Required
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            className={styles.closeButton}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          <Box className={styles.contentContainer}>
            {/* File Information */}
            <Typography variant="body1" className={styles.fileMessage}>
              We could not auto-detect a tag for the file <strong>{fileName}</strong>. Please select a primary tag to continue.
            </Typography>

            {/* Primary Tag Selection */}
            <Box className={styles.primaryTagSection}>
              <Typography variant="h6" className={styles.sectionTitle}>
                Primary Tag
              </Typography>
              <FormControl
                fullWidth
                size="small"
                className={styles.primaryTagSelect}
                error={!primaryTag}
              >
                <InputLabel>Primary Tag</InputLabel>
                <Select
                  value={primaryTag}
                  onChange={handlePrimaryTagChange}
                  label="Primary Tag"
                >
                  {availableTags.map((tag) => (
                    <MenuItem key={tag.id} value={tag.id}>
                      <Box className={styles.tagOption}>
                        <span className={styles.tagIcon}>{tag.icon}</span>
                        <span className={styles.tagLabel}>{tag.label}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Secondary Tags Selection */}
            <Box className={styles.secondaryTagsSection}>
              <Typography variant="h6" className={styles.sectionTitle}>
                Secondary Tags (Optional)
              </Typography>
              <Typography variant="body2" className={styles.secondaryDescription}>
                Click to add additional tags that describe this content
              </Typography>
              <Box className={styles.tagsGrid}>
                {availableTags.map((tag) => {
                  const isSelected = secondaryTags.includes(tag.id);
                  return (
                    <Chip
                      key={tag.id}
                      label={
                        <Box className={styles.chipContent}>
                          <span className={styles.chipIcon}>{tag.icon}</span>
                          <span className={styles.chipLabel}>{tag.label}</span>
                        </Box>
                      }
                      onClick={() => handleSecondaryTagToggle(tag.id)}
                      className={`${styles.tagChip} ${isSelected ? styles.selected : ''}`}
                      color={isSelected ? tag.color : undefined}
                      variant={isSelected ? 'filled' : 'outlined'}
                    />
                  );
                })}
              </Box>
            </Box>

            {/* 4AY Processing Rules Overview */}
            <Box className={styles.processingRulesSection}>
              <Box className={styles.rulesHeader}>
                <Info className={styles.infoIcon} />
                <Typography variant="h6" className={styles.rulesTitle}>
                  4AY Processing Rules Overview
                </Typography>
              </Box>
              <Box className={styles.rulesGrid}>
                {processingRules.map((rule, index) => (
                  <Card 
                    key={index} 
                    className={styles.ruleCard} 
                    sx={{ 
                      borderLeft: `4px solid`,
                      borderLeftColor: rule.borderColor === 'primary' ? 'primary.main' : 
                                      rule.borderColor === 'secondary' ? 'secondary.main' :
                                      rule.borderColor === 'success' ? 'success.main' :
                                      rule.borderColor === 'warning' ? 'warning.main' : 'primary.main'
                    }}
                  >
                    <CardContent className={styles.ruleCardContent}>
                      <Typography variant="subtitle2" className={styles.ruleTitle}>
                        {rule.title}
                      </Typography>
                      <Typography variant="body2" className={styles.ruleDescription}>
                        {rule.description}
                      </Typography>
                      <Box className={styles.ruleTags}>
                        {rule.tags.map((tagId) => {
                          const tag = getTagById(tagId);
                          return (
                            <Chip
                              key={tagId}
                              label={
                                <Box className={styles.chipContent}>
                                  <span className={styles.chipIcon}>{tag.icon}</span>
                                  <span className={styles.chipLabel}>{tag.label}</span>
                                </Box>
                              }
                              size="small"
                              className={styles.ruleTagChip}
                              color={tag.color}
                              sx={{
                                fontSize: '0.7rem',
                                height: 24,
                              }}
                            />
                          );
                        })}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions className={styles.dialogActions}>
          <Button
            onClick={handleClose}
            className={styles.cancelButton}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={!primaryTag}
            className={styles.confirmButton}
          >
            Confirm Tag & Upload
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default ManualTagDialog;
